/**
 * 扑克牌常量定义 - 使用0-51的数字表示52张牌
 */

// 花色常量
export enum CardSuitEnum {
  SPADES = 0,    // 黑桃 ♠️
  HEARTS = 1,    // 红桃 ♥️
  CLUBS = 2,     // 梅花 ♣️
  DIAMONDS = 3,  // 方块 ♦️
}

// 点数常量 (1-13)
export enum CardRankValue {
  ACE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
  NINE = 9,
  TEN = 10,
  JACK = 11,
  QUEEN = 12,
  KING = 13,
}

// 牌面点数对应的游戏中的点数值
export const CARD_POINT_VALUES: Record<CardRankValue, number> = {
  [CardRankValue.ACE]: 1,
  [CardRankValue.TWO]: 2,
  [CardRankValue.THREE]: 3,
  [CardRankValue.FOUR]: 4,
  [CardRankValue.FIVE]: 5,
  [CardRankValue.SIX]: 6,
  [CardRankValue.SEVEN]: 7,
  [CardRankValue.EIGHT]: 8,
  [CardRankValue.NINE]: 9,
  [CardRankValue.TEN]: 10,
  [CardRankValue.JACK]: 10,
  [CardRankValue.QUEEN]: 10,
  [CardRankValue.KING]: 10,
};

// 卡牌ID常量 (0-51)
export const CardIds = {
  // A (1)
  A_SPADES: 0,    // A♠️
  A_HEARTS: 1,    // A♥️
  A_CLUBS: 2,     // A♣️
  A_DIAMONDS: 3,  // A♦️
  
  // 2
  TWO_SPADES: 4,    // 2♠️
  TWO_HEARTS: 5,    // 2♥️
  TWO_CLUBS: 6,     // 2♣️
  TWO_DIAMONDS: 7,  // 2♦️
  
  // 3
  THREE_SPADES: 8,    // 3♠️
  THREE_HEARTS: 9,    // 3♥️
  THREE_CLUBS: 10,    // 3♣️
  THREE_DIAMONDS: 11, // 3♦️
  
  // 4
  FOUR_SPADES: 12,    // 4♠️
  FOUR_HEARTS: 13,    // 4♥️
  FOUR_CLUBS: 14,     // 4♣️
  FOUR_DIAMONDS: 15,  // 4♦️
  
  // 5
  FIVE_SPADES: 16,    // 5♠️
  FIVE_HEARTS: 17,    // 5♥️
  FIVE_CLUBS: 18,     // 5♣️
  FIVE_DIAMONDS: 19,  // 5♦️
  
  // 6
  SIX_SPADES: 20,    // 6♠️
  SIX_HEARTS: 21,    // 6♥️
  SIX_CLUBS: 22,     // 6♣️
  SIX_DIAMONDS: 23,  // 6♦️
  
  // 7
  SEVEN_SPADES: 24,    // 7♠️
  SEVEN_HEARTS: 25,    // 7♥️
  SEVEN_CLUBS: 26,     // 7♣️
  SEVEN_DIAMONDS: 27,  // 7♦️
  
  // 8
  EIGHT_SPADES: 28,    // 8♠️
  EIGHT_HEARTS: 29,    // 8♥️
  EIGHT_CLUBS: 30,     // 8♣️
  EIGHT_DIAMONDS: 31,  // 8♦️
  
  // 9
  NINE_SPADES: 32,    // 9♠️
  NINE_HEARTS: 33,    // 9♥️
  NINE_CLUBS: 34,     // 9♣️
  NINE_DIAMONDS: 35,  // 9♦️
  
  // 10
  TEN_SPADES: 36,    // 10♠️
  TEN_HEARTS: 37,    // 10♥️
  TEN_CLUBS: 38,     // 10♣️
  TEN_DIAMONDS: 39,  // 10♦️
  
  // J (11)
  J_SPADES: 40,    // J♠️
  J_HEARTS: 41,    // J♥️
  J_CLUBS: 42,     // J♣️
  J_DIAMONDS: 43,  // J♦️
  
  // Q (12)
  Q_SPADES: 44,    // Q♠️
  Q_HEARTS: 45,    // Q♥️
  Q_CLUBS: 46,     // Q♣️
  Q_DIAMONDS: 47,  // Q♦️
  
  // K (13)
  K_SPADES: 48,    // K♠️
  K_HEARTS: 49,    // K♥️
  K_CLUBS: 50,     // K♣️
  K_DIAMONDS: 51,  // K♦️
  
  // 总牌数
  TOTAL_CARDS: 52
} as const;

// 导入现有卡牌类型定义
import type { CardSuit, CardRank, Card } from './cardAnimation';

/**
 * 从卡牌ID获取花色和点数
 * @param cardId 卡牌ID (0-51)
 * @returns 花色和点数
 */
export function getSuitAndRankFromCardId(cardId: number): { suit: CardSuit; rank: CardRank } {
  // 有效性检查
  if (cardId < 0 || cardId >= CardIds.TOTAL_CARDS) {
    throw new Error(`无效的卡牌ID: ${cardId}`);
  }
  
  // 获取花色 (0-3)
  const suitValue = cardId % 4;
  // 获取点数 (1-13)
  const rankValue = Math.floor(cardId / 4) + 1;
  
  // 转换花色到卡牌花色字符串
  let suit: CardSuit;
  switch (suitValue) {
    case CardSuitEnum.SPADES:
      suit = 'spades';
      break;
    case CardSuitEnum.HEARTS:
      suit = 'hearts';
      break;
    case CardSuitEnum.CLUBS:
      suit = 'clubs';
      break;
    case CardSuitEnum.DIAMONDS:
      suit = 'diamonds';
      break;
    default:
      throw new Error(`无效的花色值: ${suitValue}`);
  }
  
  // 转换点数到卡牌点数字符串
  let rank: CardRank;
  switch (rankValue) {
    case CardRankValue.ACE:
      rank = 'A';
      break;
    case CardRankValue.JACK:
      rank = 'J';
      break;
    case CardRankValue.QUEEN:
      rank = 'Q';
      break;
    case CardRankValue.KING:
      rank = 'K';
      break;
    default:
      rank = rankValue.toString() as CardRank;
  }
  
  return { suit, rank };
}

/**
 * 从卡牌ID创建卡牌对象
 * @param cardId 卡牌ID (0-51)
 * @returns 卡牌对象
 */
export function createCardFromId(cardId: number): Card {
  const { suit, rank } = getSuitAndRankFromCardId(cardId);
  // 计算牌面点数值
  let value: number;
  if (rank === 'A') value = 1;
  else if (['J', 'Q', 'K'].includes(rank)) value = 10;
  else value = parseInt(rank);
  
  return { suit, rank, value };
}

/**
 * 从卡牌ID数组获取完整的卡牌信息
 * @param cardIds 卡牌ID数组 (每个ID在0-51之间)
 * @param isFaceUp 卡牌是否正面朝上，默认为 false
 * @returns 包含 suit, rank, value, isFaceUp, cardId 的完整卡牌信息数组
 */
export function getCardsDetailFromIds(cardIds: number[], isFaceUp: boolean = false): Array<{
  suit: CardSuit;
  rank: CardRank;
  value: number;
  isFaceUp: boolean;
  cardId: number;
}> {
  return cardIds.map(cardId => {
    // 验证cardId有效性
    if (cardId < 0 || cardId > CardIds.TOTAL_CARDS - 1) {
      console.warn(`无效的卡牌ID: ${cardId}，使用默认值0`);
      cardId = 0;
    }
    
    // 使用现有方法创建卡牌对象
    const card = createCardFromId(cardId);
    
    // 返回包含cardId的完整信息
    return {
      suit: card.suit,
      rank: card.rank,
      value: card.value,
      isFaceUp: isFaceUp,
      cardId: cardId
    };
  });
}
